import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Paper,
  Typography,
  LinearProgress,
  Button,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {NavigateFunction, useLocation, useNavigate, useParams} from "react-router-dom";
import {useToolbar} from "../../components/ToolbarContext";
import {EstimateTreeDto} from "../../shared/dtos/estimate-tree.dto";
import {EstimateTreeOperationBulkUpdateRequestEntryDto, EstimateTreeOperationBulkUpdateResponseEntryDto, EstimateTreeOperationDto} from "../../shared/dtos/estimate-tree-operation.dto";
import {requireAuth} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {getClient} from "../client/clients.service";

import {ExternalCompanyDto} from "../../shared/dtos/company.dto";
import {getCompany} from "../../shared/services/company.service";
import {distributeMinutes, effectiveLaborMinutes} from "../../shared/services/operation-time.service";
import {EstimateItemizedDisplayMode, EstimateItemizedDisplayType, EstimateItemizedStateless} from "../estimate-itemized/EstimateItemizedStateless";
import { EstimateTreeOperationMethodDto } from '../../shared/dtos/estimate-tree-operation-method.dto';
import { getWorkOrder } from './work-order.service';
import { WorkOrderDto, WorkOrderStatus, WorkOrderStatusType } from './dtos/work-order.dto';
import { enqueueSnackbar } from 'notistack';
import { updateEstimateTreeOperationsBulk } from '../estimate/estimate-tree.service';
import SplitScreenLayout from '../../shared/components/SplitScreenLayout';
import EstimateMapWorkspaceComponent from '../estimate/EstimateMapWorkspaceComponent';
import {useOptionalPermissions} from '../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../shared/permissions/permissions';

import {formatAddressLine} from "../../shared/misc/location-helpers";
export type TreeWithOperation = EstimateTreeDto & {operations: EstimateTreeOperationDto[], operationMethods?: EstimateTreeOperationMethodDto[]};

type WorkOrderItemizedPageToolbarData = {
  workOrderId: number,
  displayType: EstimateItemizedDisplayType,
  displayMode: EstimateItemizedDisplayMode,
}

const WorkOrderItemizedPage: React.FC = () => {
  const {hasPermission} = useOptionalPermissions();
  const location = useLocation();
  const isExternal = location.pathname.includes('external');

  const invoiceRef = useRef<HTMLDivElement>(null);

  const routeParams = useParams<Record<string, string>>();
  const workOrderId = parseInt(routeParams["workOrderId"]!);
  const treeIdParam = routeParams["treeId"];
  const initialTreeId = treeIdParam ? parseInt(treeIdParam) : undefined;
  const navigate = useNavigate();
  const toolbarContext = useToolbar<WorkOrderItemizedPageToolbarData>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [bulkUpdateIsLoading, setBulkUpdateIsLoading] = useState<boolean>(false);
  const [workOrder, setWorkOrder] = useState<WorkOrderDto>();
  const [client, setClient] = useState<{name: string, address: string, email?: string, phoneNumber?: string}>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  // const [updatedTreesWithOperations, setUpdatedTreesWithOperations] = useState<TreeWithOperation[]>();
  const [displayMode, setDisplayMode] = useState<EstimateItemizedDisplayMode>('admin');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // SQ-522: the steppers price at the job's crew's own rate, carried on the
  // estimate data (null with no crew — the steppers then wait).
  const billedHourlyLaborRate = workOrder?.estimateData.laborRatePerHour ?? undefined;

  const canEditStatus = !isExternal && hasPermission(PermissionCode.JobsEdit) && !([WorkOrderStatus.Invoiced, WorkOrderStatus.Deleted] as WorkOrderStatusType[]).includes(workOrder?.status ?? WorkOrderStatus.Deleted);
  const canEditContent = !isExternal && hasPermission(PermissionCode.JobsEdit) && ([WorkOrderStatus.Confirmed, WorkOrderStatus.Scheduled, WorkOrderStatus.InProgress] as WorkOrderStatusType[]).includes(workOrder?.status ?? WorkOrderStatus.Deleted);
  const canCompleteJob = hasPermission(PermissionCode.JobsComplete);

  const handleEditOrSaveClick = useCallback(() => {
    if(!canEditContent) {
      enqueueSnackbar('You cannot edit this job', {variant: 'error', autoHideDuration: 5000});
      return;
    }
    setIsEditing(prev => !prev);
  }, [canEditContent, setIsEditing]);

  const handleCompleteJobClick = useCallback(() => {
    navigate(`/work-orders/${workOrderId}/complete-job`);
  }, [workOrderId, navigate]);

  const toolbarActions = useCallback((_data: WorkOrderItemizedPageToolbarData) => {
    const completableStatuses = [WorkOrderStatus.Confirmed, WorkOrderStatus.Scheduled, WorkOrderStatus.InProgress] as WorkOrderStatusType[];
    return (
      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}} >
        {canEditContent && <Button variant='outlined' color='inherit' onClick={handleEditOrSaveClick}>{isEditing ? 'Save' : 'Edit'}</Button>}
        {canCompleteJob && completableStatuses.includes(workOrder?.status ?? WorkOrderStatus.Deleted) && <Button variant='outlined' color='inherit' onClick={handleCompleteJobClick}>Complete Job</Button>}
      </div>
    );
  }, [workOrder, handleCompleteJobClick, handleEditOrSaveClick, canEditContent, canCompleteJob, isEditing]);

  useEffect(() => {
    toolbarContext.setToolbarData({workOrderId, displayType: 'work-order' as const, displayMode});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId, displayMode]);

  // Update toolbar when mode changes to reflect read-only status
  useEffect(() => {
    if (workOrder) {
      const readonlyText = !canEditStatus ? '[READ-ONLY] ' : '';
      const toolbarData = {workOrderId, displayType: 'work-order' as const, displayMode};
      toolbarContext.initToolbar('WorkOrderItemizedPage', `${readonlyText}${formatAddressLine(workOrder.estimateData.address, workOrder.estimateData.city, workOrder.estimateData.state, workOrder.estimateData.postalCode)}`, toolbarData, toolbarActions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditStatus, workOrder, displayMode, toolbarActions]);

  const internalFetchData = async (navigate: NavigateFunction, workOrderId: number) => {
    return requireAuth(navigate)
      .then(() => getWorkOrder(navigate, workOrderId))
      .then(async wo => {
        const client = await getClient(navigate, wo.clientId)
        const company = await getCompany(navigate);
        setWorkOrder(wo);
        setClient({...client, address: formatAddressLine(client.address, client.city, client.state, client.postalCode)});
        setCompany(company);
        setDisplayMode('admin')
        const toolbarData = {workOrderId, displayType: 'estimate' as const, displayMode: 'admin' as const};
        toolbarContext.initToolbar('WorkOrderItemizedPage', `${formatAddressLine(wo.estimateData.address, wo.estimateData.city, wo.estimateData.state, wo.estimateData.postalCode)}`, toolbarData, toolbarActions);
        // setUpdatedTreesWithOperations(wo.estimateData.trees.sort((a, b) => a.id - b.id));
      })
      .catch(e => {
        logError('Error fetching job', {}, e)
        enqueueSnackbar(`Error fetching job: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
  }

  const refreshData = useCallback((silently: boolean = false) => {
    setIsLoading(prev => silently ? prev : true);
    return internalFetchData(navigate, workOrderId)
      .catch(e => {
        logError('Error fetching job', {}, e)
        enqueueSnackbar(`Error fetching job: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setIsLoading(prev => silently ? prev : false)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, workOrderId, setIsLoading]);

  useEffect(() => {
    toolbarContext.initToolbar('WorkOrderItemizedPage', 'Job Summary');
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleDisplayTypeChange = useCallback((event: React.MouseEvent<HTMLElement>, displayType: string | null) => {
    if(!displayType) return;
    setDisplayMode(displayType as 'admin' | 'customer' | 'contractor')
  }, [setDisplayMode]);

  const updateTreesFromBulkUpdate = useCallback((updatedOperations: EstimateTreeOperationBulkUpdateResponseEntryDto[]) => {
    setWorkOrder(prev => ({
      ...prev!,
      estimateData: {
        ...prev!.estimateData,
        trees: prev!.estimateData.trees.map(tree => {
          const updatedTree = updatedOperations.find(op => op.treeId === tree.id);
          return updatedTree ? {...tree, operations: updatedTree.operations} : tree;
        })
      }
    }));
  }, [setWorkOrder]);

  const performBulkUpdate = useCallback(async (updates: EstimateTreeOperationBulkUpdateRequestEntryDto[], operationName: string) => {
    setBulkUpdateIsLoading(true);
    try {
      const updatedOperations = await updateEstimateTreeOperationsBulk(navigate, workOrder!.estimateId, updates);
      await refreshData(true);
      updateTreesFromBulkUpdate(updatedOperations);
      enqueueSnackbar(`${operationName} updated successfully`, {variant: 'success', autoHideDuration: 3000});
    } catch (e) {
      logError(`Error updating ${operationName.toLowerCase()}`, {}, e as Error);
      enqueueSnackbar(`Error updating ${operationName.toLowerCase()}: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setBulkUpdateIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, workOrder, updateTreesFromBulkUpdate, setBulkUpdateIsLoading]);

  // Time-first steppers (AC-267 B1, Android parity): a tap is ±15 wall-clock
  // minutes = ±15 × crew labor minutes. Updates PATCH minutes only; the
  // server derives every price and the bulk echo refreshes the display.
  const crewSize = (workOrder?.estimateData.crewSize ?? 0) > 0 ? workOrder!.estimateData.crewSize! : 1;

  const handleOperationTimeStep = useCallback(async (treeId: number, operationId: number, deltaWallMinutes: number) => {
    if (billedHourlyLaborRate == null) return;
    const operation = workOrder?.estimateData.trees
      .find(t => t.id === treeId)?.operations.find(op => op.id === operationId);
    if (!operation) return;
    const laborMinutes = Math.max(0, effectiveLaborMinutes(operation, billedHourlyLaborRate) + deltaWallMinutes * crewSize);
    const operationUpdate = {treeId, operationId, estimatedMinutes: laborMinutes, estimatedMinutesOverride: laborMinutes};
    await performBulkUpdate([operationUpdate], 'Operation time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performBulkUpdate, billedHourlyLaborRate, crewSize, workOrder]);

  const distributeAndUpdate = useCallback(async (trees: EstimateTreeDto[], deltaWallMinutes: number, operationName: string) => {
    if (billedHourlyLaborRate == null) return;
    const operations = trees.flatMap(tree => tree.operations.map(op => ({treeId: tree.id, operation: op})));
    if (operations.length === 0) return;
    const current = operations.map(({operation}) => effectiveLaborMinutes(operation, billedHourlyLaborRate));
    const newTotal = Math.max(0, current.reduce((acc, minutes) => acc + minutes, 0) + deltaWallMinutes * crewSize);
    const distributed = distributeMinutes(current, newTotal);
    const updates: EstimateTreeOperationBulkUpdateRequestEntryDto[] = operations.map(({treeId, operation}, index) => (
      {treeId, operationId: operation.id, estimatedMinutes: distributed[index], estimatedMinutesOverride: distributed[index]}));
    await performBulkUpdate(updates, operationName);
  }, [performBulkUpdate, billedHourlyLaborRate, crewSize]);

  const handleTreeTimeStep = useCallback(async (treeId: number, deltaWallMinutes: number) => {
    const tree = workOrder?.estimateData.trees.find(t => t.id === treeId);
    if (!tree) return;
    await distributeAndUpdate([tree], deltaWallMinutes, 'Tree time');
  }, [distributeAndUpdate, workOrder]);

  const handleTotalTimeStep = useCallback(async (deltaWallMinutes: number) => {
    await distributeAndUpdate(workOrder?.estimateData.trees ?? [], deltaWallMinutes, 'Total time');
  }, [distributeAndUpdate, workOrder]);

  const workOrderPreview = useMemo(() => {
    return (
    client && <Paper sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 3}}>
      {!isExternal && <ToggleButtonGroup
        color="primary"
        value={displayMode}
        exclusive
        onChange={handleDisplayTypeChange}
        style={{marginBottom: '10px'}}
      >
        <ToggleButton value="admin">Admin</ToggleButton>
        <ToggleButton value="customer">Customer</ToggleButton>
        <ToggleButton value="contractor">Contractor</ToggleButton>
      </ToggleButtonGroup>}
      <EstimateItemizedStateless 
        ref={invoiceRef} 
        company={company!} 
        client={client} 
        estimate={workOrder!.estimateData} 
        displayType='work-order' 
        displayMode={displayMode}
        displayNameAndDate={`Job #${workOrder!.id} - Date: ${new Date(workOrder!.createdAt).toLocaleDateString()}`}
       
        onOperationTimeStep={handleOperationTimeStep}
        onTreeTimeStep={handleTreeTimeStep}
        onTotalTimeStep={handleTotalTimeStep}
        crewSize={crewSize}
        crewCount={workOrder!.estimateData.crewCount}
        billedHourlyLaborRate={billedHourlyLaborRate}
        isLoading={bulkUpdateIsLoading}
        editable={isEditing}
        />
    </Paper>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    )}, [client, displayMode, company, workOrder, bulkUpdateIsLoading, canEditContent, isExternal, isEditing, billedHourlyLaborRate, handleDisplayTypeChange, handleOperationTimeStep, handleTreeTimeStep, handleTotalTimeStep]);

  const workOrderMap = (
    workOrder?.estimateData &&<EstimateMapWorkspaceComponent 
      estimate={workOrder.estimateData}
      setEstimate={setter => {
        setWorkOrder(prev => {
          const newEstimateData = setter(prev!.estimateData);
          console.log('newEstimateData', newEstimateData)
          return {...prev!, estimateData: newEstimateData}
        })
      }}
      company={company!}
      initialTreeId={initialTreeId}
      urls={{
        getBaseUrl: () => `/work-orders/${workOrderId}/itemized`,
        getSelectedTreeUrl: (treeId: number) => `/work-orders/${workOrderId}/itemized/tree/${treeId}`,
      }}
      mode={isEditing ? 'edit' : 'read-only'}
      sidebarEnabled={false}
    />
  )

  return (
    isLoading ? <LinearProgress/> :
    !workOrder?.estimateData.trees ? <Typography variant="h4">No trees found</Typography> :
    !client ? <Typography variant="h4">No client</Typography> :
    <>
      <SplitScreenLayout
        leftPanel={workOrderPreview}
        rightPanel={workOrderMap}
        breakpoint="lg"
      /> 

    </>
  );
};

export default WorkOrderItemizedPage;